import { MouseEventHandler, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Image from '@alltrails/core/components/Image';
import { usePOIProfilePhoto } from '@alltrails/explore-service/hooks/usePOIProfilePhoto';
import DropdownMenu from '@alltrails/denali/components/DropdownMenu';
import IconButton from '@alltrails/denali/components/IconButton';
import Overflow from '@alltrails/denali/icons/Overflow';
import Hide from '@alltrails/denali/icons/Hide';
import { useSelector } from '../../redux';
import useHidePOI from '../../hooks/useHidePOI';
import HidePOIConfirmation from '../POI/POIResults/HidePOIConfirmation';
import type PhotospotResultProperties from './PhotospotResultProperties';
import styles from './PhotospotPopupCard.module.scss';

type PhotospotPopupCardProps = {
  photospot: PhotospotResultProperties;
  onMouseEnter?: MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: MouseEventHandler<HTMLDivElement>;
  hasLocationsManagePermission?: boolean;
};

const PhotospotPopupCard = ({ photospot, onMouseEnter, onMouseLeave, hasLocationsManagePermission }: PhotospotPopupCardProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const intl = useIntl();
  const hiddenPOIs = useSelector(state => state.map.hiddenPOIs);
  const { ID, name, state_name, trails_count, type_label } = photospot;
  const profilePhotoUrl = usePOIProfilePhoto({ poiId: ID, width: 160, height: 180 });
  const { isHiding, showHideConfirmation, setShowHideConfirmation, onHidePOI } = useHidePOI({ ID, hiddenPOIs });

  const handleContextMenu = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    setIsDropdownOpen(true);
  };

  const actionElement = hasLocationsManagePermission ? (
    <div className={styles.adminActions}>
      <DropdownMenu
        anchor={
          <IconButton
            icon={{ Component: Overflow }}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            title={intl.formatMessage({ defaultMessage: 'Admin actions' })}
            testId="admin-actions-button"
            variant="transparent"
            size="sm"
          />
        }
        isOpen={isDropdownOpen}
        onCloseRequest={() => setIsDropdownOpen(false)}
        options={[
          {
            testId: 'hide',
            title: hiddenPOIs?.includes(ID) ? <FormattedMessage defaultMessage="Hidden" /> : <FormattedMessage defaultMessage="Hide" />,
            onClick: () => setShowHideConfirmation(true),
            leadingIcon: { icon: { Component: Hide } }
          }
        ]}
      />
    </div>
  ) : null;

  return (
    <div className={styles.container} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onContextMenu={handleContextMenu}>
      <div className={styles.imageContainer}>
        <Image alt={name} className={styles.image} fill src={profilePhotoUrl} />
      </div>
      <div className={styles.info}>
        <div className={styles.name}>{name}</div>
        <div className={styles.state}>{state_name}</div>
        <div className={styles.details}>
          <FormattedMessage defaultMessage="{trailCount, plural, one {# trail} other {# trails}}" values={{ trailCount: trails_count }} /> •{' '}
          {type_label}
        </div>
      </div>
      {actionElement}
      {showHideConfirmation && (
        <HidePOIConfirmation onHide={onHidePOI} setShowHideConfirmation={setShowHideConfirmation} isHiding={isHiding} popupCardType="photospot" />
      )}
    </div>
  );
};

export default PhotospotPopupCard;
